import React, { useState, useEffect } from 'react'
import clsx from 'clsx'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faGithub } from '@fortawesome/free-brands-svg-icons'
import EnvironmentCard from './EnvironmentCard'
import Checklist from './Checklist'
import ProgressBar from './ProgressBar'
import CommentForm from './CommentForm'
import TicketChip from './TicketChip'
import TestsStatus from './TestsStatus'
import { fetchIssue } from '../api'
import { progress, isValidated, timeAgo, REACH_GITHUB, TECHNICAL } from '../helpers'

const Step = ({ number, done = false, title, hint, children }) => (
  <section className="flex gap-3">
    <span className={clsx(
      'grid h-7 w-7 shrink-0 place-items-center rounded-full text-[12px] font-semibold tabular-nums',
      done ? 'bg-emerald-100 text-emerald-700' : 'bg-brand-50 text-brand-700'
    )}>
      {done ? <FontAwesomeIcon icon="check" className="text-[11px]" /> : number}
    </span>
    <div className="min-w-0 flex-1">
      <h3 className="m-0 text-[14px] font-semibold leading-7 text-slate-900">{title}</h3>
      {hint && <p className="m-0 text-[12.5px] leading-snug text-slate-500">{hint}</p>}
      <div className="mt-[12px]">{children}</div>
    </div>
  </section>
)

const InfoCard = ({ icon, title, note }) => (
  <div className="flex items-start gap-3 rounded-xl border-[1px] border-slate-200 bg-slate-50 px-[16px] py-[12px]">
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-[14px] text-slate-500">
      <FontAwesomeIcon icon={icon} />
    </span>
    <div className="min-w-0 flex-1 self-center">
      <div className="text-[13.5px] font-semibold leading-snug text-slate-900">{title}</div>
      {note && <div className="text-[12.5px] leading-snug text-slate-500">{note}</div>}
    </div>
  </div>
)

const Comment = ({ comment }) => {
  const name = comment.signed_by || comment.author?.login || 'GitHub'

  return (
    <div className="flex gap-3">
      {!comment.signed_by && comment.author?.avatar_url
        ? <img src={comment.author.avatar_url} alt="" className="h-8 w-8 shrink-0 rounded-full" />
        : (
          <span className={clsx(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[12px] font-bold',
            comment.signed_by ? 'bg-brand-100 text-brand-800' : 'bg-slate-100 text-slate-600'
          )}>
            {name[0].toUpperCase()}
          </span>
          )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-[13.5px] font-semibold text-slate-900">{name}</span>
          <span className="text-[12px] text-slate-500">{timeAgo(comment.created_at)}</span>
        </div>
        <div className="tw-gh-markdown mt-0.5 break-words text-[13.5px] leading-relaxed text-slate-700">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{comment.body || ''}</ReactMarkdown>
        </div>
      </div>
    </div>
  )
}

const RunPanel = ({
  issue, issuePath, commentPath, filesPath, currentName, refreshKey, saving, onToggle, onCommented, onOpenIssue,
  testsStates, savingStatus, onChangeStatus
}) => {
  const [detail, setDetail] = useState(null)
  const [notice, setNotice] = useState(null)
  const number = issue.number
  const stats  = progress(issue.points)

  // RunPanel is keyed on the issue, so only Actualiser re-runs this for the same one.
  useEffect(() => {
    setNotice(null)
    fetchIssue(issuePath, number)
      .then(data => (data && data.error ? setNotice(data.error) : setDetail(data)))
      .catch(() => setNotice(REACH_GITHUB))
  }, [number, issuePath, refreshKey])

  const appendComment = (comment) => {
    setDetail(current => current && { ...current, comments: [...(current.comments || []), comment] })
    onCommented(number)
  }

  return (
    <article>
      <header className="border-b border-slate-200 px-6 py-[16px]">
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={() => onOpenIssue(issue.number)}
            title="Voir le ticket GitHub"
            className="tw-chip cursor-pointer border-0 bg-purple-100 font-sans text-purple-800 hover:bg-purple-200 focus:outline-none"
          >
            <FontAwesomeIcon icon={faGithub} className="text-[10px] opacity-70" />
            #{issue.number}
          </button>
          {issue.type && <span className="tw-chip bg-slate-100 text-slate-700">{issue.type}</span>}
          {issue.csm && (
            <span className="tw-chip bg-blue-100 text-blue-800">
              <FontAwesomeIcon icon="user" className="text-[9px] opacity-70" />
              CSM · {issue.csm}
            </span>
          )}
          {(issue.tickets || []).map(ticket => <TicketChip key={ticket.id} ticket={ticket} />)}
        </div>
        <h2 className="m-0 mt-2 text-[16px] font-bold leading-snug tracking-tight text-slate-900">{issue.title}</h2>
      </header>

      <div className="space-y-6 p-6">
        <Step number={1} title="Se mettre en contexte" hint="Le besoin et ce qui change.">
          {issue.need && (
            <div className="tw-gh-markdown text-[13.5px] leading-relaxed text-slate-700">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{issue.need}</ReactMarkdown>
            </div>
          )}
          {(issue.changes || (!issue.need && detail?.description)) && (
            <details className={clsx('group rounded-xl bg-slate-50 px-[16px] py-[12px]', issue.need && 'mt-[12px]')}>
              <summary className="flex cursor-pointer list-none items-center gap-2 text-[13px] font-semibold text-slate-700 hover:text-slate-900 [&::-webkit-details-marker]:hidden">
                <FontAwesomeIcon icon="chevron-right" className="text-[10px] text-slate-400 transition group-open:rotate-90" />
                {issue.changes ? 'Voir ce qui change' : 'Voir le détail de la demande'}
              </summary>
              <div className="tw-gh-markdown mt-[12px] text-[13.5px] leading-relaxed text-slate-700">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{issue.changes || detail.description}</ReactMarkdown>
              </div>
            </details>
          )}
        </Step>

        <Step number={2} title="Ouvrir l’environnement de test" hint="Le lien ouvre la version de Yuman qui contient cette demande.">
          <EnvironmentCard environment={issue.environment} />
        </Step>

        {issue.technical && (
          <Step number={3} done title="Vérification technique">
            <InfoCard icon={TECHNICAL.icon} title={TECHNICAL.text} />
          </Step>
        )}

        {!issue.technical && stats.total === 0 && (
          <Step number={3} done={issue.tests_passed} title="Valider les points de test">
            {issue.tests_passed
              ? <InfoCard icon="circle-check" title="Les tests de cette demande sont validés sur GitHub." />
              : <InfoCard icon="list-check" title="Les points de test s’affichent ici dès qu’ils sont ajoutés à la demande sur GitHub." />}
          </Step>
        )}

        {!issue.technical && stats.total > 0 && (
          <Step
            number={3}
            done={stats.complete}
            title="Valider les points de test"
            hint="Cochez chaque point dès qu’il est conforme : votre prénom est ajouté sur GitHub."
          >
            <div className="mb-[12px] flex items-center gap-3">
              <ProgressBar points={issue.points} className="flex-1" />
              <span className={clsx('shrink-0 text-[12px] font-medium tabular-nums', stats.complete ? 'text-emerald-700' : 'text-slate-600')}>
                {stats.complete ? 'Tous les points sont validés' : `${stats.done} sur ${stats.total} validé${stats.done > 1 ? 's' : ''}`}
              </span>
            </div>
            <Checklist points={issue.points} saving={saving} onToggle={(point, checked) => onToggle(issue, point, checked)} />
          </Step>
        )}

        {!issue.technical && testsStates.length > 0 && (
          <Step number={4} done={isValidated(issue)} title="Donner le statut des tests" hint="Enregistré sur le tableau GitHub.">
            <TestsStatus issue={issue} states={testsStates} saving={savingStatus} onChange={onChangeStatus} />
          </Step>
        )}

        <Step
          number={!issue.technical && testsStates.length > 0 ? 5 : 4}
          title="Partager vos retours"
          hint="Une remarque sur un point ? Décrivez-la ici, elle est publiée sur GitHub à votre nom."
        >
          {notice && <p className="m-0 text-[12.5px] text-slate-600">{notice}</p>}
          {!detail && !notice && (
            <div className="flex justify-center py-6 text-slate-400"><FontAwesomeIcon icon="circle-notch" spin /></div>
          )}
          {detail && (
            <div className="space-y-4">
              {(detail.comments || []).map(comment => <Comment key={comment.id} comment={comment} />)}
              <CommentForm commentPath={commentPath} filesPath={filesPath} number={number} currentName={currentName} onPosted={appendComment} />
            </div>
          )}
        </Step>

      </div>
    </article>
  )
}

export default RunPanel
